import { Stack, SxProps, Theme, Typography, css, styled } from "@mui/material";
import { FC, ReactNode, useMemo } from "react";

import {
  DATE_DISPLAY_FORMAT,
  DATE_TIME_DISPLAY_FORMAT,
  TIME_DISPLAY_FORMAT,
  YEAR_MONTH_DISPLAY_FORMAT,
} from "@/modules/irohana-components/constants/datetime";
import { Divider } from "@/modules/irohana-components/elements/Divider";
import { divider, text } from "@/modules/irohana-components/theme/colors-new";
import { typography } from "@/modules/irohana-components/theme/typography";
import { dayjs } from "@/modules/irohana-components/utils/dayjs";
import { Dayjs, isDayjs } from "dayjs";

type TBaseProps = {
  label?: string;
  sx?: SxProps<Theme>;
  labelSx?: SxProps<Theme>;
  valueSx?: SxProps<Theme>;
};

type TTextResult = TBaseProps & {
  type?: "text";
  value?: ReactNode;
  format?: never;
};

type TDateResult = TBaseProps & {
  type: "date";
  value?: string | Dayjs | null;
  format?: string;
};

type TDateTimeResult = TBaseProps & {
  type: "datetime";
  value?: string | Dayjs | null;
  format?: string;
};

type TTimeResult = TBaseProps & {
  type: "time";
  value?: string | Dayjs | null;
  format?: string;
};

type TMonthResult = TBaseProps & {
  type: "month";
  value?: string | Dayjs | null;
  format?: string;
};

export type TResultFieldProps = TTextResult | TDateResult | TDateTimeResult | TTimeResult | TMonthResult;

const DEFAULT_FORMATS: Record<string, string> = {
  date: DATE_DISPLAY_FORMAT,
  datetime: DATE_TIME_DISPLAY_FORMAT,
  time: TIME_DISPLAY_FORMAT,
  month: YEAR_MONTH_DISPLAY_FORMAT,
};

const formatValue = (value: string | Dayjs | null | undefined, type: string, format?: string): string => {
  if (value == null || value === "") return "";
  const fmt = format || DEFAULT_FORMATS[type];
  const d = isDayjs(value) ? value : dayjs(value);
  return d.isValid() ? d.format(fmt) : String(value);
};

export const ResultField: FC<TResultFieldProps> = ({ label, value, sx, labelSx, valueSx, ...rest }) => {
  const type = rest.type ?? "text";
  const format = "format" in rest ? rest.format : undefined;

  const displayValue = useMemo((): ReactNode => {
    if (type === "text") return value as ReactNode;
    return formatValue(value as string | Dayjs | null | undefined, type, format);
  }, [value, type, format]);

  return (
    <Stack gap={1} sx={sx}>
      {label && <StyledLabel sx={labelSx}>{label}</StyledLabel>}
      <StyledValue sx={valueSx}>{displayValue}</StyledValue>
      <Divider color={divider.middle} sx={{ mt: 1 }} />
    </Stack>
  );
};

const StyledLabel = styled(Typography)`
  color: ${text.secondary};
  ${css(typography.cap12)};
`;

const StyledValue = styled("span")`
  display: block;
  color: ${text.primary};
  ${css(typography.body14Semi)};
`;
